/**
 * PATH: packages/bills/src/components/staleDeployProbe.ts
 * FILE_ID: BILLS-PKG-CMP-STALEPROBE-001
 * PURPOSE: Detect "a newer deploy rotated this tab's hashed assets away" so
 *          the PDF viewer can self-heal with the app's one-shot reload
 *          instead of silently degrading.
 *
 * WHY: Deploys land many times a day (parallel agents), and every deploy
 *      replaces the content-hashed asset files. A tab opened BEFORE a deploy
 *      still references the old hashes; when pdf.js then tries to spin its
 *      worker, the fetch 404s, `getDocument()` hangs, and PdfCanvasViewer's
 *      stall guard silently swaps in the native <iframe> viewer — which has
 *      NO text layer, NO highlight boxes and NO pick overlay (operator
 *      2026-07-25 "no bounding box nothing check whats broken": the deploy at
 *      22:24 IST rotated assets under a tab from the 19:39 bundle).
 *
 *      The global chunk-error handler already reloads on lazy-import 404s,
 *      but the worker path swallows its failure inside pdf.js, so that
 *      handler never fires. This probe closes the gap: after a pdf.js
 *      failure, HEAD our OWN build's assets — if the server no longer has
 *      them, the bundle is stale and a reload (not a fallback) is the fix.
 *
 * RELATED:
 *   - PdfCanvasViewer.tsx: calls this from its stall-guard + render-error
 *     paths, then triggers @quotech/ui's recoverFromStaleDeploy().
 *   - packages/ui/src/lib/chunkErrorHandler.ts: the one-shot reload with
 *     cooldown + pre-reload auth refresh this defers to.
 */

/**
 * True only when the server EXPLICITLY no longer serves the asset (404/410…).
 * Network failures (offline, dev-server hiccup) return false — reloading an
 * offline tab would just bounce it into a broken boot.
 */
export async function assetGone(url: string): Promise<boolean> {
  if (typeof fetch === 'undefined' || typeof window === 'undefined') return false;
  let resolved: URL;
  try {
    resolved = new URL(url, window.location.href);
  } catch {
    return false;
  }
  // file:/blob:/data: URLs (tests, exotic embeds) can't be stale on a server.
  if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return false;
  try {
    // no-store dodges the HTTP cache: hashed assets are cached "immutable",
    // and a cached 200 would mask the server-side deletion we probe for.
    const res = await fetch(resolved.toString(), { method: 'HEAD', cache: 'no-store' });
    return !res.ok;
  } catch {
    return false;
  }
}

/**
 * Probe the two assets whose loss kills the canvas viewer: the pdf.js worker
 * chunk and the viewer's own module chunk. Either one missing means a deploy
 * rotated the bundle out from under this tab.
 */
export async function pdfViewerAssetsGone(workerUrl: string, moduleUrl: string): Promise<boolean> {
  const [workerGone, moduleGone] = await Promise.all([
    assetGone(workerUrl),
    assetGone(moduleUrl),
  ]);
  return workerGone || moduleGone;
}
